import { useSelector } from 'react-redux';
import React, { useCallback, useEffect, useState } from 'react';
import { useLazyGetCalculationsQuery } from '../../apiCalculations';
import { CalculationTableParams, PrivateFilterType } from '../../calculationsTypes';
import { LogicalOperator, Operation } from '@ydistri/api-sdk';
import { Button, Row, SectionHeader, Title } from '@ydistri/ds';
import { Alert } from 'antd';
import { signalrClient } from '../../../../signalr/client';
import { plural } from '@ydistri/generic-utils';
import { styled } from 'styled-components';
import { useSelectedProjectShortName } from '../../../../hooks/useSelectedProjectShortName';
import { useAppDispatch } from '../../../../store';
import { resetCalculationFilters } from '../../calculationsSlice';

const AlertButtonWrapper = styled.div`
  margin-left: 1rem;
`;

/**
 * Checks calculation filters and detects how many calculations could be hidden because of them.
 * When only production calculations or no private calculations are shown, newly created calculations
 * could be hidden from the user. This component shows a warning about that to avoid confusion and situations
 * when the user creates a calculation but doesn't see it in the list.
 * It also provides a button to reset the filters quickly.
 */
const CalculationsHeader: React.FC = () => {
  const calculationFilters = useSelector(state => state.calculations.calculationFilters);
  const projectShortName = useSelectedProjectShortName();
  const dispatch = useAppDispatch();

  const [trigger] = useLazyGetCalculationsQuery();
  const [hiddenCalculationsCount, setHiddenCalculationsCount] = useState<number | undefined>(
    undefined,
  );

  /**
   * Loads calculation that were created after the last filter update that
   * contradict the current filters (so the ones that are not displayed) and counts them.
   */
  const reloadHiddenCalculationsCount = useCallback(() => {
    if (!calculationFilters.lastFiltersUpdateDateTime) {
      setHiddenCalculationsCount(undefined);
      return;
    }

    const params: CalculationTableParams = {
      showOwnersCalculationsOnly: true,
      conditions: [
        {
          fieldName: 'Created',
          operation: Operation.Gt,
          value: calculationFilters.lastFiltersUpdateDateTime,
        },
      ],
    };

    if (
      calculationFilters.showProductionOnly ||
      calculationFilters.showAllPrivateCalculations === PrivateFilterType.NONE
    ) {
      if (calculationFilters.showProductionOnly) {
        params.conditions?.push({
          fieldName: 'Type',
          operation: Operation.Ne,
          value: 'Production',
          logicalOperator: LogicalOperator.And,
        });
      } else {
        if (calculationFilters.showAllPrivateCalculations === PrivateFilterType.NONE) {
          params.conditions?.push({
            fieldName: 'Type',
            operation: Operation.Eq,
            value: 'Private',
            logicalOperator: LogicalOperator.And,
          });
        }
      }

      trigger(params)
        .unwrap()
        .then(calculations => {
          if (calculations.totalCount === undefined || calculations.totalCount === null) {
            setHiddenCalculationsCount(undefined);
          } else {
            setHiddenCalculationsCount(calculations.totalCount);
          }
        })
        .catch(() => {
          setHiddenCalculationsCount(undefined);
        });
    } else {
      setHiddenCalculationsCount(undefined);
    }
  }, [
    calculationFilters.lastFiltersUpdateDateTime,
    calculationFilters.showAllPrivateCalculations,
    calculationFilters.showProductionOnly,
    trigger,
  ]);

  useEffect(() => {
    reloadHiddenCalculationsCount();
  }, [reloadHiddenCalculationsCount]);

  useEffect(() => {
    signalrClient.on('CalculationStatusChanged', reloadHiddenCalculationsCount);
    signalrClient.on('TemplateConfigurationChanged', reloadHiddenCalculationsCount);

    return () => {
      signalrClient.off('CalculationStatusChanged', reloadHiddenCalculationsCount);
      signalrClient.off('TemplateConfigurationChanged', reloadHiddenCalculationsCount);
    };
  }, [reloadHiddenCalculationsCount]);

  const resetFilters = useCallback(() => {
    if (!projectShortName) return;
    dispatch(resetCalculationFilters({ projectShortName }));
  }, [dispatch, projectShortName]);

  return (
    <SectionHeader>
      <Row $gap="1rem" $alignItems="center">
        <Title $size="large">Calculations</Title>
        {hiddenCalculationsCount !== undefined && hiddenCalculationsCount > 0 && (
          <Alert
            message={`${hiddenCalculationsCount} new ${plural(hiddenCalculationsCount, 'calculation')} is hidden due to current filters`}
            type="warning"
            showIcon
            action={
              <AlertButtonWrapper>
                <Button $sizeVariant="small" $colorVariant="ghost" onClick={resetFilters}>
                  Reset filters
                </Button>
              </AlertButtonWrapper>
            }
          />
        )}
      </Row>
    </SectionHeader>
  );
};

export default CalculationsHeader;
